/**
 * Window origin (logical CSS-px) for the current Tauri window.
 *
 * Two consumers:
 *   - React components (agent-cursor-compact, voice-indicator) — call
 *     `useWindowOrigin()` to subscribe.
 *   - Non-React modules (use-dictation event listener) — call
 *     `getWindowOrigin()` for a synchronous read of the latest cached
 *     value.
 *
 * Implementation: a single hidden zustand store, populated by a one-shot
 * background subscription that listens to onMoved/onResized. The first
 * subscriber kicks off the subscription; cleanup is reference-counted so
 * the listener tears down when no consumers remain.
 *
 * Falls back to (0, 0) outside Tauri or before the first refresh. Consumers
 * that need a measured origin can check `ready`.
 */
import { useEffect } from "react";
import { create } from "zustand";
import { listen } from "@tauri-apps/api/event";
import { invoke } from "@tauri-apps/api/core";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { isTauri } from "@/lib/is-tauri";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";

type State = { x: number; y: number; ready: boolean };

const useStore = create<State>(() => ({ x: 0, y: 0, ready: !isTauri() }));

let refCount = 0;
let cleanup: (() => void) | null = null;
let refreshGeneration = 0;

type NativeWorkArea = { x: number; y: number; width: number; height: number };

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Adopt coordinates measured by a completed native geometry command. */
export function adoptWindowOrigin(origin: { x?: number; y?: number }) {
  if (!finite(origin.x) || !finite(origin.y)) return false;
  // Retire queries started before the native transition completed.
  refreshGeneration += 1;
  useStore.setState({ x: origin.x, y: origin.y, ready: true });
  return true;
}

function stampedNativeWorkArea(): NativeWorkArea | null {
  const x = window.__DENKER_WORK_AREA_X__;
  const y = window.__DENKER_WORK_AREA_Y__;
  const width = window.__DENKER_WORK_AREA_WIDTH__;
  const height = window.__DENKER_WORK_AREA_HEIGHT__;
  return finite(x) && finite(y) && finite(width) && finite(height)
    ? { x, y, width, height }
    : null;
}

/** Resolve the window's global logical origin without dividing an absolute
 * mixed-DPI desktop position by one monitor's scale factor. Managed Denker
 * overlays exactly fill the native-stamped NSScreen work area, whose logical
 * coordinates are authoritative. User-sized windows keep the Tauri fallback. */
export function resolveLogicalWindowOrigin(
  position: { x: number; y: number },
  size: { width: number; height: number },
  scaleFactor: number,
  nativeWorkArea: NativeWorkArea | null,
): { x: number; y: number } | null {
  if (!finite(scaleFactor) || scaleFactor <= 0) return null;
  const logicalWidth = size.width / scaleFactor;
  const logicalHeight = size.height / scaleFactor;
  const measuredOrigin = {
    x: position.x / scaleFactor,
    y: position.y / scaleFactor,
  };
  const measuredOriginMatches =
    nativeWorkArea &&
    Math.abs(measuredOrigin.x - nativeWorkArea.x) <= 2 &&
    Math.abs(measuredOrigin.y - nativeWorkArea.y) <= 2;
  if (
    nativeWorkArea &&
    Math.abs(logicalWidth - nativeWorkArea.width) <= 2 &&
    Math.abs(logicalHeight - nativeWorkArea.height) <= 2 &&
    // At 1x, Tauri's physical and logical desktop spaces coincide, so an
    // origin mismatch proves this is not the managed work-area overlay. At
    // mixed DPI the absolute physical origin is not safely divisible by the
    // current monitor's scale; retain NSScreen as rendering authority there.
    (measuredOriginMatches || scaleFactor !== 1)
  ) {
    return { x: nativeWorkArea.x, y: nativeWorkArea.y };
  }
  return measuredOrigin;
}

export async function refreshWindowOrigin() {
  if (!isTauri()) return;
  const generation = ++refreshGeneration;
  try {
    if (document.documentElement.hasAttribute("data-first-run-native-window")) {
      // A bounded setup host can cross monitors with different scale factors.
      // Its AppKit origin cannot be reconstructed from a Tauri physical point.
      const origin = await invoke<{ x: number; y: number } | null>(
        "first_run_card_window_origin",
      ).catch(() => null);
      if (generation !== refreshGeneration) return;
      if (origin && finite(origin.x) && finite(origin.y)) {
        useStore.setState({ ...origin, ready: true });
        return;
      }
    }
    const w = getCurrentWindow();
    const [position, size, scaleFactor] = await Promise.all([
      w.outerPosition(),
      w.innerSize(),
      w.scaleFactor(),
    ]);
    if (generation !== refreshGeneration) return;
    const origin = resolveLogicalWindowOrigin(
      position,
      size,
      scaleFactor,
      stampedNativeWorkArea(),
    );
    if (origin) useStore.setState({ ...origin, ready: true });
  } catch {
    // Leader on primary stays at (0,0) — fine.
  }
}

function attach() {
  refCount += 1;
  if (cleanup || !isTauri()) return;
  const w = getCurrentWindow();
  void refreshWindowOrigin();
  const unmovedP = w.onMoved(() => void refreshWindowOrigin());
  const unresizedP = w.onResized(() => void refreshWindowOrigin());
  const displayMetricsP = listen(
    "denker://display-metrics-changed",
    () => void refreshWindowOrigin(),
  );
  const geometryChanged = () => void refreshWindowOrigin();
  window.addEventListener("denker:first-run-geometry-changed", geometryChanged);
  cleanup = () => {
    window.removeEventListener(
      "denker:first-run-geometry-changed",
      geometryChanged,
    );
    void unmovedP.then(safeTauriUnlisten);
    void unresizedP.then(safeTauriUnlisten);
    void displayMetricsP.then(safeTauriUnlisten);
  };
}

function detach() {
  refCount = Math.max(0, refCount - 1);
  if (refCount === 0 && cleanup) {
    cleanup();
    cleanup = null;
  }
}

/** React hook: subscribes to the latest window origin. */
export function useWindowOrigin(): State {
  useEffect(() => {
    attach();
    return () => detach();
  }, []);
  return useStore();
}

/** Sync read of the most-recently-cached origin. Safe to call from any
 *  non-React module after a React mount has primed the cache. */
export function getWindowOrigin(): State {
  return useStore.getState();
}

/** Force-prime the cache. Useful for non-React modules that fire before
 *  any React subscriber has mounted (e.g. early Tauri events). Idempotent. */
export function primeWindowOrigin(): void {
  // attach() is a no-op outside Tauri; safe to call unconditionally.
  // We don't detach — primeWindowOrigin is called from non-React contexts
  // that have no tear-down hook; keep the listeners alive for the session.
  attach();
}
